import { tokenize, ParseError } from "./tokenizer.js";
/**
 * Recursive descent parser for formula expressions.
 *
 * Precedence (low to high):
 *   ||
 *   &&
 *   == !=
 *   > < >= <=
 *   + -
 *   * / %
 *   unary (! -)
 *   primary (literal, fieldRef, functionCall, grouped)
 */
export function parse(expression) {
    const tokens = tokenize(expression);
    const parser = new Parser(tokens);
    const ast = parser.parseExpression();
    parser.expect("eof");
    return ast;
}
class Parser {
    tokens;
    pos = 0;
    depth = 0;
    maxDepth = 50;
    constructor(tokens) {
        this.tokens = tokens;
    }
    parseExpression() {
        this.depth++;
        if (this.depth > this.maxDepth) {
            throw new ParseError("Expression exceeds maximum nesting depth", this.current().position);
        }
        const result = this.logicalOr();
        this.depth--;
        return result;
    }
    // --- Precedence levels ---
    logicalOr() {
        let left = this.logicalAnd();
        while (this.match("||")) {
            const right = this.logicalAnd();
            left = { type: "binaryOp", op: "||", left, right };
        }
        return left;
    }
    logicalAnd() {
        let left = this.equality();
        while (this.match("&&")) {
            const right = this.equality();
            left = { type: "binaryOp", op: "&&", left, right };
        }
        return left;
    }
    equality() {
        let left = this.comparison();
        while (this.matchAny(["==", "!="])) {
            const op = this.tokens[this.pos - 1].value;
            const right = this.comparison();
            left = { type: "binaryOp", op, left, right };
        }
        return left;
    }
    comparison() {
        let left = this.addition();
        while (this.matchAny([">", "<", ">=", "<="])) {
            const op = this.tokens[this.pos - 1].value;
            const right = this.addition();
            left = { type: "binaryOp", op, left, right };
        }
        return left;
    }
    addition() {
        let left = this.multiplication();
        while (this.matchAny(["+", "-"])) {
            const op = this.tokens[this.pos - 1].value;
            const right = this.multiplication();
            left = { type: "binaryOp", op, left, right };
        }
        return left;
    }
    multiplication() {
        let left = this.unary();
        while (this.matchAny(["*", "/", "%"])) {
            const op = this.tokens[this.pos - 1].value;
            const right = this.unary();
            left = { type: "binaryOp", op, left, right };
        }
        return left;
    }
    unary() {
        if (this.match("!")) {
            const operand = this.unary();
            return { type: "unaryOp", op: "!", operand };
        }
        if (this.match("-")) {
            const operand = this.unary();
            return { type: "unaryOp", op: "-", operand };
        }
        return this.primary();
    }
    primary() {
        const token = this.current();
        // Number literal
        if (token.type === "number") {
            this.advance();
            if (token.value === "true")
                return { type: "literal", value: true };
            if (token.value === "false")
                return { type: "literal", value: false };
            if (token.value === "null")
                return { type: "literal", value: null };
            return { type: "literal", value: Number(token.value) };
        }
        // String literal
        if (token.type === "string") {
            this.advance();
            return { type: "literal", value: token.value };
        }
        // Field reference
        if (token.type === "fieldRef") {
            this.advance();
            return { type: "fieldRef", name: token.value };
        }
        // Function call
        if (token.type === "function") {
            const name = token.value;
            this.advance();
            this.expect("lparen");
            const args = [];
            if (this.current().type !== "rparen") {
                args.push(this.parseExpression());
                while (this.current().type === "comma") {
                    this.advance(); // skip comma
                    args.push(this.parseExpression());
                }
            }
            this.expect("rparen");
            return { type: "functionCall", name, args };
        }
        // Grouped expression
        if (token.type === "lparen") {
            this.advance();
            const expr = this.parseExpression();
            this.expect("rparen");
            return expr;
        }
        throw new ParseError(`Unexpected token '${token.value}' (${token.type}) at position ${token.position}`, token.position);
    }
    // --- Helpers ---
    current() {
        return this.tokens[this.pos] ?? { type: "eof", value: "", position: -1 };
    }
    advance() {
        const t = this.current();
        this.pos++;
        return t;
    }
    match(value) {
        if (this.current().type === "operator" && this.current().value === value) {
            this.advance();
            return true;
        }
        return false;
    }
    matchAny(values) {
        const token = this.current();
        if (token.type === "operator" && values.includes(token.value)) {
            this.advance();
            return true;
        }
        return false;
    }
    expect(type) {
        const token = this.current();
        if (token.type === type) {
            this.advance();
            return;
        }
        throw new ParseError(`Expected ${type} but got '${token.value}' (${token.type}) at position ${token.position}`, token.position);
    }
}
//# sourceMappingURL=parser.js.map